import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@folio/components';
import { cn, formatDate } from '@folio/lib';
import type { EmailThreadDto, EmailThreadLinkedPolicyDto } from 'korint-sdk';
import { Loader2, Lock, Users, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EMAIL_ACCOUNT_STATUS_DOT } from './emailAccountStatus';
import { Text } from '@folio/components/ui/text';

export function MailboxIndicator({
  account,
  compact = false,
}: {
  account: EmailThreadDto['account'];
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const status = t(`connectedEmailAccounts.status.${account.status}`);
  const label = t(account.isShared ? 'generalInbox.mailbox.shared' : 'generalInbox.mailbox.own');

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex max-w-full items-center gap-1.5',
            !compact &&
              'rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground',
          )}
        >
          <span
            role="img"
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              EMAIL_ACCOUNT_STATUS_DOT[account.status],
            )}
            aria-label={status}
          />
          {compact ? (
            <span className="sr-only">
              {label} · {account.primaryEmailAddress}
            </span>
          ) : (
            <>
              {account.isShared ? (
                <Users aria-hidden className="size-3 shrink-0" />
              ) : (
                <Lock aria-hidden className="size-3 shrink-0" />
              )}
              <Text inline truncate>
                {account.primaryEmailAddress}
              </Text>
            </>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {label} · {status}
      </TooltipContent>
    </Tooltip>
  );
}

// Without a count it labels a single email
export function ToAttachBadge({ count }: { count?: number }) {
  const { t } = useTranslation();
  if (count === 0) return null;
  return (
    <Badge
      variant="outline"
      className="shrink-0 whitespace-nowrap border-amber-300 bg-amber-50 px-2 font-medium text-amber-800"
      data-testid="general-inbox-to-attach"
    >
      {count === undefined
        ? t('generalInbox.toAttach')
        : t('generalInbox.toAttachCount', { count })}
    </Badge>
  );
}

export function AnalysisPending() {
  const { t } = useTranslation();
  return (
    <span
      role="status"
      title={t('generalInbox.analysisPending')}
      data-testid="general-inbox-analysis-pending"
    >
      <Loader2 className="size-3.5 animate-spin text-muted-foreground" aria-hidden />
      <span className="sr-only">{t('generalInbox.analysisPending')}</span>
    </span>
  );
}

// A quote has no number yet: it reads as its client and product, never as the internal id
export function usePolicyLabel() {
  const { t } = useTranslation();
  const join = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(' · ');
  const product = ({ productId }: EmailThreadLinkedPolicyDto) =>
    productId && t(`products.${productId}.label`, { defaultValue: '' });
  const reference = ({ externalId }: EmailThreadLinkedPolicyDto) =>
    externalId ?? t('generalInbox.policyLinks.quote');
  return {
    label: (policy: EmailThreadLinkedPolicyDto) =>
      policy.externalId ?? join(reference(policy), policy.customerName, product(policy)),
    title: (policy: EmailThreadLinkedPolicyDto) => policy.customerName ?? reference(policy),
    details: (policy: EmailThreadLinkedPolicyDto) =>
      join(policy.customerName && reference(policy), product(policy)),
  };
}

export function LinkedPolicyBadges({
  policies,
  onDetach,
  disabled,
}: {
  policies: EmailThreadDto['linkedPolicies'];
  onDetach?: (policy: EmailThreadLinkedPolicyDto) => void;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { label } = usePolicyLabel();
  return policies.map(policy => (
    <Badge
      key={policy.id}
      variant="outline"
      className="relative z-10 min-w-0 max-w-full gap-1 bg-card transition-colors hover:border-primary/40"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={`/policies/${policy.id}`}
            className="min-w-0 truncate rounded-sm outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="general-inbox-linked-policy"
          >
            {label(policy)}
          </Link>
        </TooltipTrigger>
        {policy.createdAt && (
          <TooltipContent>
            {t('generalInbox.policyLinks.createdAt', {
              date: formatDate(new Date(policy.createdAt), i18n.language),
            })}
          </TooltipContent>
        )}
      </Tooltip>
      {onDetach && (
        <button
          type="button"
          className="-mr-1 shrink-0 rounded-full p-0.5 text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          aria-label={t('generalInbox.policyLinks.detach', { policy: label(policy) })}
          disabled={disabled}
          onClick={() => onDetach(policy)}
          data-testid="general-inbox-detach-policy"
        >
          <X className="size-3" aria-hidden />
        </button>
      )}
    </Badge>
  ));
}
